import React, { useState } from 'react';
import {
  Breadcrumbs, Button, Edit01, EmptyState, Table, Trash01,
} from '@opendate-app/setlist';
import CrmPageLayout from '../../shared/components/CrmPageLayout';
import CrmSearchInput from '../../shared/components/CrmSearchInput';
import AttributeFormModal from './AttributeFormModal';
import OrderFormFields from '../../TicketingSettings/components/OrderFormFields';
import client from '../../../shared/api/client';
import { toastSuccess, toastError } from '../../../shared/toastHelper';

function CrmAttributesPage({ team, objectType, attributes: initialAttributes = [], venueOwnerships = null, selectedVenueOwnership = null, eventOrderFormAvailable = false }) {
  const [attributes, setAttributes] = useState(initialAttributes);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);

  const teamId = team && team.id;
  const isOrderType = objectType === 'Order';
  const showRequired = objectType !== 'Donation';
  const showVoPicker = isOrderType && venueOwnerships && !selectedVenueOwnership;

  const baseUrl = teamId ? `/teams/${teamId}/crm/custom_fields` : null;

  const getIconForFieldType = (fieldType) => {
    const lowerType = (fieldType || '').toLowerCase();
    if (lowerType.includes('text') || lowerType.includes('area')) return 'fal fa-font';
    if (lowerType.includes('address')) return 'fal fa-map-marker-alt';
    if (lowerType.includes('phone')) return 'fal fa-phone';
    if (lowerType.includes('email')) return 'fal fa-envelope';
    if (lowerType.includes('date') || lowerType.includes('time')) return 'fal fa-calendar';
    if (lowerType.includes('number') || lowerType.includes('integer')) return 'fal fa-hashtag';
    if (lowerType.includes('boolean') || lowerType.includes('checkbox')) return 'fal fa-check-square';
    if (lowerType.includes('select') || lowerType.includes('option')) return 'fal fa-list';
    if (lowerType.includes('association')) return 'fal fa-link';
    return 'fal fa-cube';
  };

  const formatFieldType = (type) => {
    if (!type) return '';
    return type.replace('CustomFields::', '').replace(/([A-Z])/g, ' $1').trim();
  };

  const filteredAttributes = attributes.filter((attr) =>
    attr.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatObjectTypeName = (type) => {
    if (!type) return 'All Objects';
    if (type === 'Fan') return 'Contacts';
    if (type === 'Donation') return 'Gifts';
    return type.replace(/([A-Z])/g, ' $1').trim();
  };

  const settingsUrl = teamId ? `/teams/${teamId}/crm-settings` : null;
  const objectsUrl = teamId ? `/teams/${teamId}/crm-objects` : null;
  const objectTypeUrl = teamId && objectType
    ? (objectType === 'Order' ? `/teams/${teamId}/order-attributes` : `/teams/${teamId}/crm-attributes/${encodeURIComponent(objectType)}`)
    : null;

  const getVenueOwnershipUrl = (voId) => {
    if (!teamId) return null;
    return `/teams/${teamId}/order-attributes/venue_ownerships/${voId}`;
  };

  const handleCreateAttribute = async (data) => {
    if (!baseUrl) return;

    try {
      const payload = {
        custom_fieldable_type: objectType,
        name: data.name,
        type: data.attributeType,
        enabled: data.enabled,
        required: data.required,
        ...(data.associationType ? { association_type: data.associationType } : {}),
        ...(data.allowMultiple !== undefined ? { allow_multiple: data.allowMultiple } : {}),
      };
      if (data.customFieldOptions) {
        payload.custom_field_options_attributes = data.customFieldOptions.map((o, i) => ({
          ...(o.id ? { id: o.id } : {}),
          name: o.name,
          position: o.position || i + 1,
          ...(o._destroy ? { _destroy: true } : {}),
        }));
      }
      const response = await client.post(`${baseUrl}.json`, { custom_field: payload });
      setAttributes((prev) => [...prev, response.data]);
      toastSuccess('Attribute created');
      return true;
    } catch (error) {
      const messages = error.response?.data;
      toastError(Array.isArray(messages) ? messages.join(', ') : 'Failed to create attribute');
    }
  };

  const handleEditAttribute = async (data) => {
    if (!baseUrl || !editingAttribute) return;

    try {
      const payload = {
        name: data.name,
        type: data.attributeType,
        enabled: data.enabled,
        required: data.required,
        ...(data.associationType ? { association_type: data.associationType } : {}),
        ...(data.allowMultiple !== undefined ? { allow_multiple: data.allowMultiple } : {}),
      };
      if (data.customFieldOptions) {
        payload.custom_field_options_attributes = data.customFieldOptions.map((o, i) => ({
          ...(o.id ? { id: o.id } : {}),
          name: o.name,
          position: o.position || i + 1,
          ...(o._destroy ? { _destroy: true } : {}),
        }));
      }
      const response = await client.patch(`${baseUrl}/${editingAttribute.id}.json`, { custom_field: payload });
      setAttributes((prev) =>
        prev.map((a) => (a.id === editingAttribute.id ? response.data : a))
      );
      toastSuccess('Attribute updated');
      return true;
    } catch (error) {
      const messages = error.response?.data;
      toastError(Array.isArray(messages) ? messages.join(', ') : 'Failed to update attribute');
    }
  };

  const handleDelete = async (attr) => {
    if (!baseUrl) return;
    if (!window.confirm(`Delete "${attr.name}"? This cannot be undone.`)) return;

    try {
      await client.delete(`${baseUrl}/${attr.id}.json`);
      setAttributes((prev) => prev.filter((a) => a.id !== attr.id));
      toastSuccess('Attribute deleted');
    } catch (error) {
      toastError('Failed to delete attribute');
    }
  };

  const renderCheck = (value) => (value ? (
    <span className="text-success"><i className="fal fa-check" aria-hidden="true" /><span className="sr-only">Yes</span></span>
  ) : (
    <span className="text-muted"><i className="fal fa-times" aria-hidden="true" /><span className="sr-only">No</span></span>
  ));

  const breadcrumb = (
    <Breadcrumbs
      items={[
        { label: 'Settings', href: settingsUrl },
        { label: 'Objects', href: objectsUrl },
        selectedVenueOwnership
          ? { label: formatObjectTypeName(objectType), href: objectTypeUrl }
          : { label: formatObjectTypeName(objectType) },
      ]}
    />
  );

  if (showVoPicker) {
    return (
      <CrmPageLayout
        team={team}
        activeSidebarKey="settings"
        mainTopMargin={32}
        breadcrumb={breadcrumb}
        title="Attributes"
        subtitle="Select a venue to manage its custom fields."
      >
        <div className="card">
          <div className="card-body p-0">
            <Table
              columns={[
                {
                  key: 'name',
                  header: 'Venue',
                  width: '30%',
                  primary: true,
                  render: (vo) => (
                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                      <span
                        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light text-muted"
                        style={{ width: 32, height: 32, minWidth: 32 }}
                      >
                        <i className="far fa-map-marker-alt" aria-hidden="true" />
                      </span>
                      <Button as="a" href={getVenueOwnershipUrl(vo.id)} variant="link-color">
                        {vo.name}
                      </Button>
                    </div>
                  ),
                },
                { key: 'location', header: 'Location', width: '30%', render: (vo) => <span className="text-muted">{vo.location}</span> },
                { key: 'upcoming_events', header: 'Upcoming Events', width: '15%', render: (vo) => vo.upcoming_events_count || 0 },
                { key: 'attributes', header: 'Attributes', width: '15%', render: (vo) => vo.attributes_count || 0 },
              ]}
              data={venueOwnerships}
              getRowKey={(vo) => vo.id}
              empty={<EmptyState size="sm" title="No venues found." />}
            />
          </div>
        </div>
      </CrmPageLayout>
    );
  }

  return (
    <CrmPageLayout
      team={team}
      activeSidebarKey="settings"
      mainTopMargin={32}
      breadcrumb={breadcrumb}
      title={selectedVenueOwnership ? selectedVenueOwnership.name : 'Attributes'}
      subtitle={selectedVenueOwnership
        ? "Manage the information you collect during checkout. These fields apply to every event at this venue."
        : 'Modify and add object attributes.'}
      primaryAction={isOrderType ? null : {
        label: 'Create attribute',
        iconClass: 'fa fa-plus',
        onClick: () => setIsModalOpen(true),
      }}
    >
      {selectedVenueOwnership ? (
        <OrderFormFields
          team={team}
          askPerAvailable={eventOrderFormAvailable}
          venueOwnership={{ id: selectedVenueOwnership.id, custom_fields: attributes }}
          customFieldsChanged={(_venueOwnershipId, customFields) => setAttributes(customFields)}
          venueName={selectedVenueOwnership.name}
        />
      ) : (
      <div className="card">
        <div className="card-body p-0">
          <div className="border-bottom px-4 py-3">
            <CrmSearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search attributes"
              ariaLabel="Search attributes"
              useDebounce={false}
              shortcutLabel={null}
            />
          </div>

          <Table
            columns={[
              { key: 'name', header: 'Name', primary: true, render: (attr) => <span className="figma-text sm semibold">{attr.name}</span> },
              {
                key: 'type',
                header: 'Type',
                width: '15%',
                render: (attr) => (
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <span className="text-muted">
                      <i className={getIconForFieldType(attr.type)} aria-hidden="true" />
                    </span>
                    <span>{formatFieldType(attr.type)}</span>
                  </div>
                ),
              },
              { key: 'enabled', header: 'Enabled', width: '1%', render: (attr) => renderCheck(attr.enabled) },
              ...(showRequired ? [{ key: 'required', header: 'Required', width: '1%', render: (attr) => renderCheck(attr.required) }] : []),
              {
                key: 'actions',
                header: '',
                align: 'right',
                width: '1%',
                render: (attr) => (
                  <div className="d-flex justify-content-end text-nowrap">
                    <Button
                      variant="link-gray"
                      size="sm"
                      icon={Edit01}
                      title="Edit attribute"
                      aria-label="Edit attribute"
                      onClick={() => setEditingAttribute(attr)}
                    />
                    <Button
                      variant="link-destructive"
                      size="sm"
                      icon={Trash01}
                      title="Delete attribute"
                      aria-label="Delete attribute"
                      onClick={() => handleDelete(attr)}
                      className="ml-2"
                    />
                  </div>
                ),
              },
            ]}
            data={filteredAttributes}
            getRowKey={(attr) => attr.id}
            loadingLabel="Loading attributes..."
            empty={<EmptyState size="sm" title="No attributes found." />}
          />
        </div>
      </div>
      )}

      <AttributeFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateAttribute}
        objectType={objectType}
      />

      <AttributeFormModal
        isOpen={!!editingAttribute}
        onClose={() => setEditingAttribute(null)}
        onSubmit={handleEditAttribute}
        attribute={editingAttribute}
        objectType={objectType}
      />
    </CrmPageLayout>
  );
}

export default CrmAttributesPage;
